import { MASKED_SIGNATURE_TEXT, MAX_SIGNATURE_TEXT_LENGTH, type Signature } from '@belvdr/shared'
import { BLOCK_CLASS, MASK_CLASS } from './masking.js'

/**
 * Signature d'un élément (voir `CONTEXT.md`) : rôle, nom accessible, identifiant stable, testId
 * et contexte, calculés sur le DOM vivant. Jamais de valeur saisie ; les libellés sous
 * `.belvdr-mask` sont masqués et rien n'est décrit sous `.belvdr-block`, comme dans le Recording.
 */

/** Éléments sur lesquels un clic est une Action du visiteur. */
const ACTIONNABLES = [
  'button',
  'a[href]',
  'input[type=button]',
  'input[type=submit]',
  'input[type=reset]',
  'input[type=image]',
  'input[type=checkbox]',
  'input[type=radio]',
  'select',
  'summary',
  ...[
    'button',
    'link',
    'menuitem',
    'menuitemcheckbox',
    'menuitemradio',
    'tab',
    'checkbox',
    'radio',
    'switch',
    'option',
    'treeitem',
  ].map((role) => `[role=${role}]`),
].join(', ')

/** Conteneurs dont le titre sert de contexte (carte, section, ligne, formulaire, dialogue). */
const CONTENEURS = [
  'article',
  'section',
  'li',
  'tr',
  'fieldset',
  'form',
  'dialog',
  ...['article', 'region', 'listitem', 'row', 'group', 'dialog', 'form'].map(
    (role) => `[role=${role}]`,
  ),
].join(', ')

/** Titres d'un conteneur, dans l'ordre du document. */
const TITRES = 'h1, h2, h3, h4, h5, h6, legend, caption'

/** Conteneurs remontés au plus pour trouver un titre. */
const MAX_CONTENEURS = 3

/** Rôles implicites des balises courantes (sinon, la balise elle-même). */
const ROLES_IMPLICITES: Record<string, string> = {
  button: 'button',
  select: 'combobox',
  textarea: 'textbox',
  summary: 'button',
  form: 'form',
  dialog: 'dialog',
  p: 'paragraph',
  h1: 'heading',
  h2: 'heading',
  h3: 'heading',
  h4: 'heading',
  h5: 'heading',
  h6: 'heading',
  ul: 'list',
  ol: 'list',
  li: 'listitem',
  nav: 'navigation',
  main: 'main',
  header: 'banner',
  footer: 'contentinfo',
  aside: 'complementary',
  section: 'region',
  article: 'article',
  table: 'table',
  tr: 'row',
  td: 'cell',
  th: 'columnheader',
  img: 'img',
  output: 'status',
  progress: 'progressbar',
  details: 'group',
  fieldset: 'group',
}

const ROLES_INPUT: Record<string, string> = {
  button: 'button',
  submit: 'button',
  reset: 'button',
  image: 'button',
  checkbox: 'checkbox',
  radio: 'radio',
  range: 'slider',
}

function roleDe(element: Element): string {
  const explicite = element.getAttribute('role')?.trim().split(/\s+/)[0]
  if (explicite) return explicite
  const tag = element.tagName.toLowerCase()
  if (tag === 'a') return element.hasAttribute('href') ? 'link' : 'a'
  if (tag === 'input') return ROLES_INPUT[(element as HTMLInputElement).type] ?? 'textbox'
  return ROLES_IMPLICITES[tag] ?? tag
}

/** Texte normalisé d'une Signature : espaces réduits, chiffres remplacés, 60 caractères au plus. */
function normaliser(texte: string | null | undefined): string | undefined {
  const propre = (texte ?? '').replace(/\s+/g, ' ').trim().replace(/\d+/g, '#')
  if (propre === '') return undefined
  return propre.length > MAX_SIGNATURE_TEXT_LENGTH
    ? `${propre.slice(0, MAX_SIGNATURE_TEXT_LENGTH - 1)}…`
    : propre
}

const estMasque = (element: Element) => element.closest(`.${MASK_CLASS}`) !== null

/** Libellé d'un élément : texte masqué s'il est sous `.belvdr-mask`. */
function libelle(element: Element, texte: string | null | undefined): string | undefined {
  const nom = normaliser(texte)
  return nom !== undefined && estMasque(element) ? MASKED_SIGNATURE_TEXT : nom
}

/** Nom donné par `aria-label` ou `aria-labelledby` (seuls noms des éléments non actionnables). */
function nomAria(element: Element): string | undefined {
  const label = libelle(element, element.getAttribute('aria-label'))
  if (label !== undefined) return label
  const ids = element.getAttribute('aria-labelledby')?.trim().split(/\s+/) ?? []
  const references = ids
    .map((id) => element.ownerDocument.getElementById(id))
    .filter((reference) => reference !== null)
  if (references.length === 0) return undefined
  const texte = references.map((reference) => reference.textContent).join(' ')
  const nom = normaliser(texte)
  if (nom === undefined) return undefined
  return estMasque(element) || references.some(estMasque) ? MASKED_SIGNATURE_TEXT : nom
}

/** Nom accessible d'un élément actionnable (jamais la valeur saisie d'un champ). */
function nomActionnable(element: Element): string | undefined {
  const aria = nomAria(element)
  if (aria !== undefined) return aria
  if (element instanceof HTMLInputElement) {
    if (['button', 'submit', 'reset'].includes(element.type))
      return libelle(element, element.getAttribute('value'))
    if (element.type === 'image') return libelle(element, element.getAttribute('alt'))
  }
  if (
    element instanceof HTMLInputElement ||
    element instanceof HTMLSelectElement ||
    element instanceof HTMLTextAreaElement
  ) {
    const label = element.labels?.[0]
    return label ? libelle(label, label.textContent) : undefined
  }
  return (
    libelle(element, element.textContent) ??
    libelle(element, element.getAttribute('title')) ??
    libelle(element, element.querySelector('img[alt]')?.getAttribute('alt')) ??
    libelle(element, element.querySelector('svg title')?.textContent)
  )
}

/**
 * Identifiant stable : commence par une lettre, sans suite de 3 chiffres ni allure
 * d'hexadécimal (`:r1:`, `ember1234`, `item-0199a1b2`, `f3a9c2e1b7d4` sont générés).
 */
function idStable(id: string): string | undefined {
  if (!/^[A-Za-z][\w-]*$/.test(id) || id.length > 64) return undefined
  if (/\d{3,}/.test(id)) return undefined
  if (/^[0-9a-f]{8,}$/i.test(id) && /\d/.test(id)) return undefined
  return id
}

function testIdDe(element: Element): string | undefined {
  const value =
    element.getAttribute('data-testid') ??
    element.getAttribute('data-test') ??
    element.getAttribute('data-cy')
  return value === null || value.trim() === '' ? undefined : value.trim().slice(0, 100)
}

/** Titre du conteneur le plus proche qui en a un (au plus 3 conteneurs remontés). */
function contexteDe(element: Element): string | undefined {
  let conteneur = element.parentElement?.closest(CONTENEURS) ?? null
  for (let i = 0; conteneur !== null && i < MAX_CONTENEURS; i++) {
    const aria = nomAria(conteneur)
    if (aria !== undefined) return aria
    const titre = conteneur.querySelector(TITRES)
    const texte = titre ? libelle(titre, titre.textContent) : undefined
    if (texte !== undefined) return texte
    conteneur = conteneur.parentElement?.closest(CONTENEURS) ?? null
  }
  return undefined
}

/** Rôles des éléments notables : leur apparition ou leur contenu signale un effet de la page. */
const ROLES_NOTABLES = new Set([
  'alert',
  'status',
  'dialog',
  'alertdialog',
  'log',
  'progressbar',
  'tooltip',
])

/** Éléments à examiner pour trouver les notables (sélecteur large, affiné par `estNotable`). */
export const CANDIDATS_NOTABLES =
  '[id], [data-testid], [data-test], [data-cy], [role], [aria-live], dialog, output, progress'

/**
 * Élément notable : identifiant stable, testId, rôle d'annonce (`alert`, `status`, `dialog`…),
 * région `aria-live` ou `dialog`. Son Apparition ou son Changement de contenu est un Fait.
 */
export function estNotable(element: Element): boolean {
  if (element.id !== '' && idStable(element.id) !== undefined) return true
  if (testIdDe(element) !== undefined) return true
  if (ROLES_NOTABLES.has(roleDe(element))) return true
  const live = element.getAttribute('aria-live')
  return (live !== null && live !== 'off') || element.tagName === 'DIALOG'
}

/** L'élément notable le plus proche d'un nœud (lui-même ou un ancêtre), hors `html` et `body`. */
export function notableLePlusProche(node: Node): Element | null {
  let element = node instanceof Element ? node : node.parentElement
  while (element !== null && element !== element.ownerDocument.body) {
    if (element === element.ownerDocument.documentElement) return null
    if (estNotable(element)) return element
    element = element.parentElement
  }
  return null
}

/** L'élément actionnable visé par un clic, depuis sa cible (icône d'un bouton…), ou `null`. */
export function elementActionnable(target: EventTarget | null): Element | null {
  if (!(target instanceof Element)) return null
  return target.closest(ACTIONNABLES)
}

/** Signature d'un élément, ou `null` s'il est sous `.belvdr-block` (jamais décrit). */
export function signatureDe(element: Element): Signature | null {
  if (element.closest(`.${BLOCK_CLASS}`) !== null) return null
  const signature: Signature = { role: roleDe(element) }
  const id = element.id === '' ? undefined : idStable(element.id)
  if (id !== undefined) signature.id = id
  const testId = testIdDe(element)
  if (testId !== undefined) signature.testId = testId
  const nom = element.matches(ACTIONNABLES) ? nomActionnable(element) : nomAria(element)
  if (nom !== undefined) signature.nom = nom
  const contexte = contexteDe(element)
  if (contexte !== undefined) signature.contexte = contexte
  return signature
}
